import {Formik} from "formik";
import React, {FunctionComponent, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {AdminConfigurationSchema} from "../../../../constants/validation/admin/AdminConfigurationValidationSchema";
import {AdminConfigurationDto} from "../../../../interfaces/AdminConfigurationInterfaces";
import {adminConfigurationService} from "../../../../services/AdminConfigurationService";
import {ColorType} from "../../../../types/bootstrap/BootstrapType";
import {dateUtils} from "../../../../utils/dateUtils";
import {toastUtils} from "../../../../utils/toastUtils";
import Button from "../../../atoms/Button";
import FormInput from "../../input/FormInput";
import GenericModal from "../../modal/GenericModal";
import EpowForm from "../EpowForm";
import {Label} from "reactstrap";
import {optionUtils} from "../../../../utils/optionUtils";
import FormSelect from "../../input/FormSelect";
import {MONTHS_OPTIONS} from "../../../../constants/OptionConstants";
import {useIntlOptions} from "../../../../hooks/useIntlOptions";

interface AdminConfigurationFormProps {
  className?: string,
  onUpdate: () => void,
  onCancel: () => void,
  adminConfiguration: AdminConfigurationDto,
}

const AdminConfigurationForm: FunctionComponent<AdminConfigurationFormProps> = ({
  className = "",
  onUpdate,
  onCancel,
  adminConfiguration,
}) => {
  const intl = useIntl();
  const initialValues: AdminConfigurationDto = {
    dashboardBookingPendingWindow: 0,
    dashboardBookingTodoWindow: 0,
    dashboardStaffingWindow: 0,
    bookingPreBookingDelay: 10,
    bookingServiceDelay: 30,
    departureTimeMinuteDelta: 15,
    staffingAmplitude: "01:30",
    accountingPeriodStartDay: undefined,
    accountingPeriodStartMonth: undefined,
    accountingPeriodEndDay: undefined,
    accountingPeriodEndMonth: undefined,
  }

  const daysOptions = optionUtils.getOptionNumberListFromTo(1, 31)
  const monthsOptions = useIntlOptions(intl, MONTHS_OPTIONS)

  const [amplitudeConfirmation, setAmplitudeConfirmation] = useState<AdminConfigurationDto>(undefined)

  const saveConfiguration = (formValue: AdminConfigurationDto) => {
    setAmplitudeConfirmation(undefined)
    adminConfigurationService.updateAdminConfiguration(formValue)
      .then(() => {
        onUpdate()
        toastUtils.successToast(intl.formatMessage({id: "success_toast_update_admin_settings"}))
      })
      .catch(() => {
        toastUtils.errorToast(intl.formatMessage({id: "error_toast_save_admin_config"}))
      })
  }

  const handleSubmit = (formValue: AdminConfigurationDto) => {
    const amplitudeChanged = dateUtils.getTimeWithoutSeconds(formValue?.staffingAmplitude)
      !== dateUtils.getTimeWithoutSeconds(adminConfiguration?.staffingAmplitude)

    if (amplitudeChanged) {
      setAmplitudeConfirmation(formValue)
    } else {
      saveConfiguration(formValue)
    }
  }

  return (
    <Formik initialValues={adminConfiguration ?? initialValues} validationSchema={AdminConfigurationSchema} onSubmit={handleSubmit}>
      {({errors, touched, values, setFieldValue}) => (
        <EpowForm id={"admin-config"} className={className}>
          <div className="col mb-2">
            <FormInput
              id="dashboardBookingTodoWindow"
              label="admin_config_dashboard_booking_todo"
              popover="admin_config_dashboard_booking_todo_tooltip"
              type="number"
              value={values?.dashboardBookingTodoWindow}
              onChange={(e) => setFieldValue("dashboardBookingTodoWindow", e.target.value)}
              required
              error={errors.dashboardBookingTodoWindow}
              touched={touched.dashboardBookingTodoWindow}
            />

            <FormInput
              id="dashboardBookingPendingWindow"
              label="admin_config_dashboard_booking_pending"
              popover="admin_config_dashboard_booking_pending_tooltip"
              type="number"
              value={values?.dashboardBookingPendingWindow}
              onChange={(e) => setFieldValue("dashboardBookingPendingWindow", e.target.value)}
              required
              error={errors.dashboardBookingPendingWindow}
              touched={touched.dashboardBookingPendingWindow}
            />

            <FormInput
              id="dashboardStaffingWindow"
              label="admin_config_dashboard_staffing"
              popover="admin_config_dashboard_staffing_tooltip"
              type="number"
              value={values?.dashboardStaffingWindow}
              onChange={(e) => setFieldValue("dashboardStaffingWindow", e.target.value)}
              required
              error={errors.dashboardStaffingWindow}
              touched={touched.dashboardStaffingWindow}
            />

            <FormInput
              id="bookingPreBookingDelay"
              label="admin_config_booking_prebooking_delay"
              popover="admin_config_booking_prebooking_delay_tooltip"
              type="number"
              value={values?.bookingPreBookingDelay}
              onChange={(e) => setFieldValue("bookingPreBookingDelay", e.target.value)}
              required
              error={errors.bookingPreBookingDelay}
              touched={touched.bookingPreBookingDelay}
            />

            <FormInput
              id="bookingServiceDelay"
              label="admin_config_booking_service_delay"
              popover="admin_config_booking_service_delay_tooltip"
              type="number"
              value={values?.bookingServiceDelay}
              onChange={(e) => setFieldValue("bookingServiceDelay", e.target.value)}
              required
              error={errors.bookingServiceDelay}
              touched={touched.bookingServiceDelay}
            />

            <FormInput
              id="departureTimeMinuteDelta"
              label="admin_config_booking_delta_minutes"
              popover="admin_config_booking_delta_minutes_tooltip"
              type="number"
              maxValue="60"
              value={values?.departureTimeMinuteDelta}
              onChange={(e) => setFieldValue("departureTimeMinuteDelta", e.target.value)}
              required
              error={errors.departureTimeMinuteDelta}
              touched={touched.departureTimeMinuteDelta}
            />

            <FormInput
              id="staffingAmplitude"
              label="admin_config_staffing_amplitude"
              popover="admin_config_staffing_amplitude_tooltip"
              type="time"
              step="60"
              value={dateUtils.getTimeWithoutSeconds(values?.staffingAmplitude)}
              onChange={(e) => setFieldValue("staffingAmplitude", e.target.value)}
              required
              error={errors.staffingAmplitude}
              touched={touched.staffingAmplitude}
            />

            <div>

              <Label  className="epow-label"><FormattedMessage id="admin_config_accounting_period" /></Label>
              <div className="d-flex gap-2">
                <FormSelect
                  id="dayDateBeginDay"
                  options={daysOptions}
                  onChange={(option) => setFieldValue("accountingPeriodStartDay", option.value)}
                  label="filter_from"
                  value={daysOptions.find((option) => option?.value === values?.accountingPeriodStartDay)}
                  placeholder={intl.formatMessage({id: "day"})}
                  required
                  error={errors.accountingPeriodStartDay}
                  touched={touched.accountingPeriodStartDay}
                />
                <FormSelect
                  id="dayDateBeginMonth"
                  options={monthsOptions}
                  label=" "
                  onChange={(option) => setFieldValue("accountingPeriodStartMonth", parseInt(option.value as string))}
                  value={monthsOptions.find((option) => option?.value === values?.accountingPeriodStartMonth?.toString())}
                  placeholder={intl.formatMessage({id: "month"})}
                  error={errors.accountingPeriodStartMonth}
                  touched={touched.accountingPeriodStartMonth}
                  required
                />
                <span className="bold fs-20 d-flex align-items-center">-</span>
                <FormSelect
                  id="dayDateEndDay"
                  options={daysOptions}
                  onChange={(option) => setFieldValue("accountingPeriodEndDay", option.value)}
                  label="filter_to"
                  value={daysOptions.find((option) => option?.value === values?.accountingPeriodEndDay)}
                  placeholder={intl.formatMessage({id: "day"})}
                  required
                  error={errors.accountingPeriodEndDay}
                  touched={touched.accountingPeriodEndDay}
                />
                <FormSelect
                  id="dayDateEndMonth"
                  options={monthsOptions}
                  label=" "
                  onChange={(option) => setFieldValue("accountingPeriodEndMonth", parseInt(option.value as string))}
                  value={monthsOptions.find((option) => option?.value === values?.accountingPeriodEndMonth?.toString())}
                  placeholder={intl.formatMessage({id: "month"})}
                  error={errors.accountingPeriodEndMonth}
                  touched={touched.accountingPeriodEndMonth}
                  required
                />
              </div>
            </div>
          </div>

          <Button className="me-2" color={ColorType.SECONDARY} onClick={onCancel} >
            <FormattedMessage id="cancel_button" />
          </Button>

          <Button type="submit" >
            <FormattedMessage id="save_button" />
          </Button>

          <GenericModal
            open={!!amplitudeConfirmation}
            onOpen={() => setAmplitudeConfirmation(undefined)}
            title={intl.formatMessage({id: "modal_title_update_staffing_amplitude"})}
            closable
            footer={{
              primaryAction: {
                buttonLabel: intl.formatMessage({id: "validate_button"}),
                action: () => saveConfiguration(amplitudeConfirmation),
              },
              secondaryAction: {
                buttonLabel: intl.formatMessage({id: "cancel_button"}),
                action: () => setAmplitudeConfirmation(undefined),
              },
            }}
          >
            {intl.formatMessage({id: "modal_body_update_staffing_amplitude"})}
          </GenericModal>

        </EpowForm>
      )}
    </Formik>
  )
}

export default AdminConfigurationForm;
